import { useNode } from "@craftjs/core";
import { ImageList, ImageListItem } from "@mui/material";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import useApplyGlobalSettings, { CODEBLOCK_TYPES } from "../../../../hooks/useApplyGlobalSettings";
import { usePaletteColor } from "../../../../hooks/usePaletteColor";
import { getSelectedSegment } from "../../../../redux/selectors/segmentBuilderSelectors";
import {
  addRandomColsAndRows,
  dummyGridImageObject,
  getVariant,
  getVariantProps,
} from "../../../../utils/helpers";
import { BlockIndicator } from "../../Shared/BlockIndicator";
import ImageBlock from "../../Shared/ImageBlock";
import ImageGridSettings from "./ImageGridSettings";

const Container = styled.div`
  padding: ${(props) => props.padding};
  display: flex;
  flex-wrap: wrap;
  justify-content: ${(props) => props.alignment};
  gap: ${(props) => props.gap || "10px"};

  .MuiImageList-root {
    margin-bottom: 0px !important;
  }
`;

const GRID_TYPES = {
  STANDARD: "standard",
  QUILTED: "quilted",
  MASONRY: "masonry",
};

export const ImageGrid = ({
  images,
  showHyperlink,
  hyperlink,
  isApplyMarginOnAllSides,
  allSideMargin,
  margin,
  borderRadius,
  borderColor,
  borderWidth,
  dropShadowColor,
  alignment,
  gridType,
  showCaptions,
  columns,
}) => {
  const {
    connectors: { connect, drag },
    actions: { setProp },
  } = useNode();

  const selectedSegment = useSelector(getSelectedSegment);
  const selectedSegmentColor = selectedSegment?.settings?.color;
  const { imageGrid: imageGridProps } = useSelector((state) => state.codeblockStyles);
  const dispatch = useDispatch();

  const { getPaletteColor } = usePaletteColor();

  useEffect(() => {
    // Set the props from the reducer if they're undefined
    const timeoutId = setTimeout(() => {
      setProp((props) => {
        // Margin properties
        if (props.margin === undefined) {
          props.margin = { ...imageGridProps.margin };
        }
        
        if (props.isApplyMarginOnAllSides === undefined) {
          props.isApplyMarginOnAllSides = imageGridProps.isApplyMarginOnAllSides;
        }
        
        if (props.allSideMargin === undefined) {
          props.allSideMargin = imageGridProps.allSideMargin;
        }
        
        // Border properties
        if (props.borderRadius === undefined) {
          props.borderRadius = imageGridProps.borderRadius;
        }
        
        if (props.borderWidth === undefined) {
          props.borderWidth = imageGridProps.borderWidth;
        }
        
        if (props.borderColor === undefined) {
          props.borderColor = imageGridProps.borderColor || "suggested_color_0";
        }
        
        // Other style properties
        if (props.dropShadowColor === undefined) {
          props.dropShadowColor = imageGridProps.dropShadowColor;
        }
        
        if (props.alignment === undefined) {
          props.alignment = imageGridProps.alignment;
        }
        
        // ImageGrid-specific properties
        if (props.showCaptions === undefined) {
          props.showCaptions = imageGridProps.showCaptions;
        }
        
        if (props.gridType === undefined) {
          props.gridType = imageGridProps.gridType;
        }
        
        if (props.columns === undefined) {
          props.columns = imageGridProps.columns;
        }
      });
    }, 5);
    
    // Cleanup timeout if the component unmounts or dependencies change
    return () => clearTimeout(timeoutId);
  }, []);

  // Use the hook to apply global settings when they change
  useApplyGlobalSettings(setProp, CODEBLOCK_TYPES.ImageGrid);

  const marginStyle = isApplyMarginOnAllSides
    ? `${allSideMargin}px`
    : margin
    ? `${margin?.top}px ${margin?.right}px ${margin?.bottom}px ${margin?.left}px`
    : undefined;

  const border = borderColor
    ? `${borderWidth}px solid ${getPaletteColor(borderColor)}`
    : "none";

  const renderImageBlock = (images = []) => {
    return (
      <ImageList
        cols={images.length < columns ? images.length : columns}
        variant={getVariant(gridType, GRID_TYPES)}
        gap={gridType === GRID_TYPES.MASONRY ? 8 : undefined}
      >
        {images.map((item) => {
          const { srcSet, src, cols, rows } = getVariantProps(
            item,
            gridType,
            GRID_TYPES
          );
          return (
            <ImageListItem key={item.src} cols={cols} rows={rows}>
              <ImageBlock
                key={item.src}
                srcSet={srcSet}
                src={src}
                title={item.title}
                showImageTitle={showCaptions}
                showHyperlink={showHyperlink}
                hyperlink={hyperlink}
                style={{
                  boxShadow:
                    dropShadowColor &&
                    `0px 0px 9.3px 0px ${getPaletteColor(dropShadowColor)}`,
                  borderRadius,
                  border,
                }}
                alt={item.title}
                isImageLoading={item?.loading}
              />
            </ImageListItem>
          );
        })}
      </ImageList>
    );
  };

  return (
    <div ref={(ref) => connect(drag(ref))}>
      <BlockIndicator>
        <Container
          padding={marginStyle}
          alignment={alignment}
          dropShadowColor={dropShadowColor}
        >
          <div>
            {images?.length ? (
              renderImageBlock(
                gridType == GRID_TYPES.QUILTED
                  ? addRandomColsAndRows(images)
                  : images
              )
            ) : (
              <>
                <ImageBlock
                  key={dummyGridImageObject.src}
                  src={dummyGridImageObject.src}
                  title={dummyGridImageObject.title}
                  showImageTitle={showCaptions}
                  style={{
                    borderRadius,
                    border,
                  }}
                  alt={dummyGridImageObject.title}
                  loading="lazy"
                />
              </>
            )}
          </div>
        </Container>
      </BlockIndicator>
    </div>
  );
};

export const ImageGridDefaultProps = {
  images: [],
  shallowImages: [],
  margin: undefined,
  isApplyMarginOnAllSides: undefined,
  allSideMargin: undefined,
  borderRadius: undefined,
  borderWidth: undefined,
  showCaptions: undefined,
  dropShadowColor: undefined,
  alignment: undefined,
  gap: "10px",
  gridType: undefined,
  borderColor: undefined,
  columns: undefined,
};

ImageGrid.craft = {
  props: ImageGridDefaultProps,
  related: {
    settings: ImageGridSettings,
  },
};
